/**
 * Payment / clinic calendar dates as YYYY-MM-DD using the **laptop/browser
 * timezone** (automatic via the JS Date local getters).
 *
 * Backend stores paymentDate as Asia/Colombo midnight. On a Sri Lanka PC that
 * matches. Avoid `iso.split("T")[0]` — that uses the **UTC** calendar day and
 * shifts Colombo midnights back one day (e.g. 2026-09-06T00:00+05:30 → "2026-09-05").
 *
 * Check in DevTools console:
 *   Intl.DateTimeFormat().resolvedOptions().timeZone  // e.g. "Asia/Colombo"
 *   new Date("2026-09-05T18:30:00.000Z").toString()     // local wall clock
 *   // UTC wrong:  "2026-09-05T18:30:00.000Z".split("T")[0] → "2026-09-05"
 *   // Local right on SL laptop → "2026-09-06"
 */

export function toLocalYmd(
  value: Date | string | undefined | null
): string {
  if (value == null || value === "") return "";

  // Already a plain calendar date from <input type="date" />
  if (typeof value === "string") {
    const plain = value.trim().match(/^(\d{4}-\d{2}-\d{2})$/);
    if (plain) return plain[1];
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Today as YYYY-MM-DD in the laptop/browser timezone. */
export function todayLocalYmd(): string {
  return toLocalYmd(new Date());
}

/** IANA zone for PDF reports — pass as `timeZone` so the server matches the PC. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Colombo";
  } catch {
    return "Asia/Colombo";
  }
}

/** @deprecated Use toLocalYmd — kept for call-site clarity during rename. */
export const toColomboYmd = toLocalYmd;
/** @deprecated Use todayLocalYmd */
export const todayColomboYmd = todayLocalYmd;
